import React from "react";
import { useTranslation } from "react-i18next";

import {
  useProjectRetrieval,
  useRetrieveKanban,
  useTaskStatusesRetrieval,
} from "@planzone/view-models";
import { Status, status } from "@planzone/dto";
import { ActivityWatcher } from "@common/activityWatcher";
import { ColorPicker } from "@common/colorPicker";
import { Loader } from "@common/loader";
import { Modal } from "@common/modal";
import { CloseIcon } from "./closeIcon";
import { Statuses } from "./statuses";
import { Props } from "./type";
import styles from "./styles.scss";

function TaskStatuses(props: Props) {
  const { t } = useTranslation();
  const { project, isRequestPending, isRequestSuccess, retrieveProject } =
    useProjectRetrieval(props.project.uuid);
  const {
    statuses,
    isRequestPending: isTaskStatusesRetrievalRequestPending,
    isRequestSuccess: isTaskStatusesRetrievalRequestSuccess,
    retrieveTaskStatuses,
  } = useTaskStatusesRetrieval(project);
  const {
    isRequestPending: isRequestKanbanPending,
    kanban,
    retrieveKanban,
  } = useRetrieveKanban(project);
  const tasks = kanban.columns.map((column) => column.tasks).flat();
  const [colorPickerState, setColorPickerState] = React.useState<{
    isDisplayed: boolean;
    y: number;
    status: Status;
    setStatus: (status: Status) => void;
  }>({
    isDisplayed: false,
    y: 0,
    status: status,
    setStatus: () => {},
  });

  React.useEffect(() => {
    !isRequestKanbanPending && retrieveKanban(project);
  }, []);

  React.useEffect(() => {
    isRequestSuccess &&
      !isTaskStatusesRetrievalRequestPending &&
      !isTaskStatusesRetrievalRequestSuccess &&
      retrieveTaskStatuses(project);
  }, [isRequestSuccess, project]);

  React.useEffect(() => {
    !isRequestPending && retrieveProject(props.project.uuid);
  }, [props.project]);

  return (
    <Modal header={t("projectTaskStatuses.title")} close={props.cancel}>
      <div className={styles.main}>
        <div className={styles.form}>
          <div className={styles.statuses}>
            {!isTaskStatusesRetrievalRequestPending &&
            !isRequestKanbanPending ? (
              <Statuses
                project={project}
                statuses={statuses}
                tasks={tasks}
                displayColorPickerCreation={(y, status, setStatus) =>
                  setColorPickerState({
                    ...colorPickerState,
                    isDisplayed: true,
                    y,
                    status,
                    setStatus,
                  })
                }
              />
            ) : (
              <div className={styles.loader}>
                <Loader />
              </div>
            )}
          </div>
        </div>
        {colorPickerState.isDisplayed && (
          <ActivityWatcher
            blur={() =>
              setColorPickerState({ ...colorPickerState, isDisplayed: false })
            }
          >
            <div
              className={styles.colorPicker}
              style={{
                left: -230,
                top: colorPickerState.y - 200,
              }}
            >
              <ColorPicker
                color={colorPickerState.status.color}
                pick={(color) => {
                  setColorPickerState({
                    ...colorPickerState,
                    status: { ...colorPickerState.status, color },
                  });
                  colorPickerState.setStatus({
                    ...colorPickerState.status,
                    color: color,
                  });
                }}
              />
            </div>
          </ActivityWatcher>
        )}
      </div>
    </Modal>
  );
}

export { TaskStatuses };
